export type CardPriority = 'Critical' | 'High' | 'Medium' | 'Low';
export type CardStatus = 'Backlog' | 'To Do' | 'In Progress' | 'In Review' | 'Done';

export interface KanbanLabel {
  id: string;
  name: string;
  color: string;
}

export interface KanbanMember {
  id: string;
  name: string;
  initials: string;
  color: string;
}

export interface KanbanCard {
  id: string;
  title: string;
  description: string;
  status: CardStatus;
  priority: CardPriority;
  assignee: KanbanMember | null;
  storyPoints: number;
  dueDate: string;
  labels: KanbanLabel[];
  columnId: string;
  overdue?: boolean;
}

export interface KanbanColumn {
  id: string;
  title: CardStatus;
  wipLimit: number | null;
  color: string;
}

export const KANBAN_MEMBERS: KanbanMember[] = [
  { id: 'km-001', name: 'Alex Morgan', initials: 'AM', color: '#6366F1' },
  { id: 'km-002', name: 'Priya Sharma', initials: 'PS', color: '#34D399' },
  { id: 'km-003', name: 'Jordan Lee', initials: 'JL', color: '#FBBF24' },
  { id: 'km-004', name: 'Casey Rivera', initials: 'CR', color: '#F87171' },
  { id: 'km-005', name: 'Sam Okafor', initials: 'SO', color: '#818CF8' },
];

export const KANBAN_LABELS: KanbanLabel[] = [
  { id: 'lbl-001', name: 'Frontend', color: '#818CF8' },
  { id: 'lbl-002', name: 'Backend', color: '#34D399' },
  { id: 'lbl-003', name: 'Design', color: '#FB923C' },
  { id: 'lbl-004', name: 'Bug', color: '#F87171' },
  { id: 'lbl-005', name: 'Feature', color: '#6366F1' },
  { id: 'lbl-006', name: 'Infra', color: '#FBBF24' },
  { id: 'lbl-007', name: 'Security', color: '#EC4899' },
];

export const KANBAN_COLUMNS: KanbanColumn[] = [
  { id: 'col-backlog', title: 'Backlog', wipLimit: null, color: '#6B6B80' },
  { id: 'col-todo', title: 'To Do', wipLimit: 8, color: '#6366F1' },
  { id: 'col-inprogress', title: 'In Progress', wipLimit: 5, color: '#FBBF24' },
  { id: 'col-done', title: 'Done', wipLimit: null, color: '#34D399' },
];

export const INITIAL_CARDS: KanbanCard[] = [
  {
    id: 'card-001',
    title: 'Redesign onboarding flow for mobile',
    description: 'Complete overhaul of 5-step onboarding with new illustrations and micro-animations.',
    status: 'Backlog',
    priority: 'High',
    assignee: KANBAN_MEMBERS[1],
    storyPoints: 8,
    dueDate: 'May 25, 2026',
    labels: [KANBAN_LABELS[0], KANBAN_LABELS[2]],
    columnId: 'col-backlog',
  },
  {
    id: 'card-002',
    title: 'Fix push notification delivery on iOS 17',
    description: 'Investigate and fix silent notification failures affecting ~12% of iOS users.',
    status: 'Backlog',
    priority: 'Critical',
    assignee: KANBAN_MEMBERS[0],
    storyPoints: 5,
    dueDate: 'May 15, 2026',
    labels: [KANBAN_LABELS[3]],
    columnId: 'col-backlog',
    overdue: true,
  },
  {
    id: 'card-003',
    title: 'Add dark mode to settings screen',
    description: 'Implement system-aware dark mode toggle with smooth transition.',
    status: 'Backlog',
    priority: 'Medium',
    assignee: KANBAN_MEMBERS[2],
    storyPoints: 3,
    dueDate: 'Jun 1, 2026',
    labels: [KANBAN_LABELS[0], KANBAN_LABELS[2]],
    columnId: 'col-backlog',
  },
  {
    id: 'card-004',
    title: 'Implement GraphQL subscriptions for live updates',
    description: 'Replace polling with WebSocket subscriptions for project and card state.',
    status: 'To Do',
    priority: 'High',
    assignee: KANBAN_MEMBERS[4],
    storyPoints: 13,
    dueDate: 'May 30, 2026',
    labels: [KANBAN_LABELS[1]],
    columnId: 'col-todo',
  },
  {
    id: 'card-005',
    title: 'Write unit tests for auth module',
    description: 'Achieve 80% coverage on login, signup, token refresh, and revocation flows.',
    status: 'To Do',
    priority: 'Medium',
    assignee: KANBAN_MEMBERS[0],
    storyPoints: 5,
    dueDate: 'Jun 5, 2026',
    labels: [KANBAN_LABELS[1]],
    columnId: 'col-todo',
  },
  {
    id: 'card-006',
    title: 'Set up CI/CD pipeline with GitHub Actions',
    description: 'Automate build, test, and deploy to staging on every PR merge.',
    status: 'To Do',
    priority: 'High',
    assignee: KANBAN_MEMBERS[3],
    storyPoints: 8,
    dueDate: 'May 22, 2026',
    labels: [KANBAN_LABELS[5]],
    columnId: 'col-todo',
    overdue: true,
  },
  {
    id: 'card-007',
    title: 'Drag-and-drop card reordering in Kanban',
    description: 'Implement @dnd-kit based drag-and-drop with optimistic updates and rollback.',
    status: 'In Progress',
    priority: 'High',
    assignee: KANBAN_MEMBERS[0],
    storyPoints: 8,
    dueDate: 'May 18, 2026',
    labels: [KANBAN_LABELS[0], KANBAN_LABELS[4]],
    columnId: 'col-inprogress',
  },
  {
    id: 'card-008',
    title: 'Migrate user table to Postgres 16',
    description: 'Upgrade DB version, run migration scripts, validate indexes and constraints.',
    status: 'In Progress',
    priority: 'Critical',
    assignee: KANBAN_MEMBERS[4],
    storyPoints: 13,
    dueDate: 'May 16, 2026',
    labels: [KANBAN_LABELS[1], KANBAN_LABELS[5]],
    columnId: 'col-inprogress',
    overdue: true,
  },
  {
    id: 'card-009',
    title: 'Localization: add French and German strings',
    description: 'Extract all UI strings to i18n files and add fr-FR and de-DE translations.',
    status: 'In Progress',
    priority: 'Medium',
    assignee: KANBAN_MEMBERS[2],
    storyPoints: 5,
    dueDate: 'Jun 10, 2026',
    labels: [KANBAN_LABELS[0]],
    columnId: 'col-inprogress',
  },
  {
    id: 'card-010',
    title: 'Stripe billing integration — subscription management',
    description: 'Implement plan selection, payment flow, and webhook handling for upgrades/cancellations.',
    status: 'Done',
    priority: 'High',
    assignee: KANBAN_MEMBERS[3],
    storyPoints: 13,
    dueDate: 'May 10, 2026',
    labels: [KANBAN_LABELS[1], KANBAN_LABELS[4]],
    columnId: 'col-done',
  },
  {
    id: 'card-011',
    title: 'Design system token audit',
    description: 'Audit all color, spacing, and typography tokens. Remove duplicates and document usage.',
    status: 'Done',
    priority: 'Low',
    assignee: KANBAN_MEMBERS[1],
    storyPoints: 3,
    dueDate: 'May 8, 2026',
    labels: [KANBAN_LABELS[2]],
    columnId: 'col-done',
  },
  {
    id: 'card-012',
    title: 'Rate limiting on API endpoints',
    description: 'Implement Redis-backed rate limiting with per-user and per-IP buckets.',
    status: 'Done',
    priority: 'High',
    assignee: KANBAN_MEMBERS[4],
    storyPoints: 8,
    dueDate: 'May 5, 2026',
    labels: [KANBAN_LABELS[1], KANBAN_LABELS[6]],
    columnId: 'col-done',
  },
];